/**
 * Column Width Configuration
 *
 * Defines width constraints for ArTable columns based on field type.
 * Uses CSS Grid minmax() for automatic sizing with bounds.
 */

import { UIElementType } from '#types/System.types';
import type { ArColumnDefinition } from '#types/System.Artifacts.types';

export type ColumnWidthConfig = {
  min: number;
  max: number | 'max-content';
};

/**
 * Width configuration for field columns by typeId.
 * - Numeric/date: fit content with safety bounds
 * - Text: fixed range for ellipsis truncation
 */
const FIELD_WIDTH_CONFIG: Record<string, ColumnWidthConfig> = {
  // Numeric types: narrow, fit content
  number: { min: 60, max: 'max-content' },
  quantity: { min: 60, max: 'max-content' },
  currency: { min: 80, max: 'max-content' },
  percentage: { min: 60, max: 'max-content' },
  sellPriceUI: { min: 180, max: 'max-content' },

  // Date/time types: fit content
  date: { min: 90, max: 'max-content' },
  dateTime: { min: 140, max: 'max-content' },
  dateRange: { min: 160, max: 'max-content' },
  time: { min: 70, max: 'max-content' },
  timeRange: { min: 120, max: 'max-content' },
  dateTimeRange: { min: 200, max: 'max-content' },

  // Text types: fixed range with ellipsis
  text: { min: 100, max: 250 },
  shortText: { min: 80, max: 200 },
  longText: { min: 120, max: 300 },
  richText: { min: 150, max: 400 },

  // Selection types
  singleSelect: { min: 80, max: 180 },
  multiSelect: { min: 100, max: 250 },

  // Boolean
  yesNo: { min: 50, max: 100 },

  // Address
  address: { min: 150, max: 300 },
};

/**
 * Width configuration for reference columns by referenceType.
 */
const REFERENCE_WIDTH_CONFIG: Record<string, ColumnWidthConfig> = {
  entity: { min: 120, max: 'max-content' },
  form: { min: 100, max: 200 },
  artifact: { min: 100, max: 200 },
};

const DEFAULT_WIDTH_CONFIG: ColumnWidthConfig = { min: 100, max: 'max-content' };

/**
 * Gets the width configuration for a column based on its type.
 */
export const getColumnWidthConfig = (column: ArColumnDefinition): ColumnWidthConfig => {
  if (column.uiElementType === UIElementType.Field) {
    return FIELD_WIDTH_CONFIG[column.typeId] ?? DEFAULT_WIDTH_CONFIG;
  }

  if (column.uiElementType === UIElementType.Reference) {
    return REFERENCE_WIDTH_CONFIG[column.referenceType] ?? DEFAULT_WIDTH_CONFIG;
  }

  return DEFAULT_WIDTH_CONFIG;
};

/**
 * Generates a CSS Grid track string for a column.
 * If userWidth is provided, returns a fixed pixel width (clamped to bounds).
 * Otherwise returns minmax() for automatic sizing.
 */
export const getColumnGridTrack = (
  column: ArColumnDefinition,
  userWidth?: number,
): string => {
  const config = getColumnWidthConfig(column);

  if (userWidth !== undefined) {
    const maxPx = config.max === 'max-content' ? 600 : config.max;
    const clampedWidth = Math.max(config.min, Math.min(userWidth, maxPx));
    return `${clampedWidth}px`;
  }

  const maxValue = config.max === 'max-content' ? 'max-content' : `${config.max}px`;
  return `minmax(${config.min}px, ${maxValue})`;
};

/**
 * Returns a deterministic pixel width for a column.
 *
 * Notes:
 * - Used to keep frozen column `left` offsets stable without DOM measurement.
 * - When no userWidth is provided, uses the width config:
 *   - numeric max → use max (intentionally matches truncation ranges for text/select)
 *   - max-content → use min (keeps narrow types compact by default)
 */
export const getColumnPixelWidth = (
  column: ArColumnDefinition,
  userWidth?: number
): number => {
  const config = getColumnWidthConfig(column);

  const maxPx = config.max === 'max-content' ? 600 : config.max;

  if (userWidth !== undefined) {
    return Math.max(config.min, Math.min(userWidth, maxPx));
  }

  const defaultWidth = config.max === 'max-content' ? config.min : config.max;
  return Math.max(config.min, Math.min(defaultWidth, maxPx));
};

/**
 * Builds a columnWidthConfig map for all columns.
 * Used by ArTableHeader for resize handle bounds.
 */
export const buildColumnWidthConfigMap = (
  columns: ArColumnDefinition[]
): Record<string, { min: number; max: number }> => {
  const result: Record<string, { min: number; max: number }> = {};

  for (const column of columns) {
    const config = getColumnWidthConfig(column);
    result[column.typeId] = {
      min: config.min,
      max: config.max === 'max-content' ? 600 : config.max,
    };
  }

  return result;
};

/**
 * Builds a CSS Grid template for PDF export from type-based widths only —
 * intentionally independent of any user-resized widths on screen, so the PDF
 * is consistent across users regardless of who exported it.
 *
 * Each column emits `minmax(min-content, naturalFr)`:
 * - `min-content` floors every track at its own widest unbreakable token (longest word
 *   or widest nowrap number), so a column is never squeezed below what fits without
 *   breaking a word mid-character, yet still collapses to the narrowest no-break width —
 *   minimising the grid's total min-content and therefore the fit-to-page downscale.
 * - `naturalFr` is the column's natural pixel width (`getColumnPixelWidth(col)` with no
 *   `userWidth`) used as an `fr` weight, so CSS Grid stretches the tracks proportionally
 *   to fill the table's full container width — the table always uses the full PDF page width.
 */
export const buildPdfGridTemplate = (columns: ArColumnDefinition[]): string =>
  columns
    .map((col) => `minmax(min-content, ${getColumnPixelWidth(col)}fr)`)
    .join(' ');
